import { AnalysisResult, AnalyzeWebsiteParams, LogEntry, GoodUrl } from '../types/analysis';
import { normalizeAnalysisData } from './dataUtils';

/// <reference types="vite/client" />
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://buildswarm-api.speedppc.workers.dev';
const API_KEY = import.meta.env.VITE_API_KEY || import.meta.env.VITE_DEFAULT_API_KEY || 'dev-api-key-1234';

if (!API_KEY) {
  console.warn('API_KEY environment variable is not set. Using default development key.');
}

// Enhanced logging for API configuration
console.log('Environment variables:', {
  VITE_API_URL: import.meta.env.VITE_API_URL || 'not set',
  VITE_API_KEY: import.meta.env.VITE_API_KEY ? 'set' : 'not set',
  VITE_DEFAULT_API_KEY: import.meta.env.VITE_DEFAULT_API_KEY ? 'set' : 'not set',
});
console.log(`API configured with URL: ${API_BASE_URL}`);
console.log(`API Key configured: ${API_KEY ? 'Yes' : 'No'}`);

interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // No longer need to throw error as we now have a default API key
  const headers = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error((error as { message: string }).message || `HTTP error! status: ${response.status}`);
    }

    return response.json() as Promise<T>;
  } catch (error) {
    console.error(`API error for ${endpoint}:`, error);
    throw error;
  }
}

export async function analyzeWebsite(params: AnalyzeWebsiteParams): Promise<AnalysisResult> {
  // Store the request payload for debugging
  const requestPayload = JSON.stringify(params);
  console.log('Analysis request payload:', requestPayload);
  
  const response = await fetch(`${API_BASE_URL}/analyze`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: requestPayload
  });

  if (!response.ok) {
    // Get the error details and status code
    const errorStatus = response.status;
    let errorData;
    
    try {
      errorData = await response.json();
    } catch (parseError) {
      // If we can't parse JSON, use a default error object
      errorData = { message: `HTTP error: ${response.statusText}` };
    }
    
    // Throw error with detailed information
    const error = new Error(errorData.message || `Failed to analyze website (HTTP ${errorStatus})`);
    // Add custom properties to the error object
    (error as any).statusCode = errorStatus;
    (error as any).responseData = errorData;
    (error as any).requestPayload = requestPayload;
    throw error;
  }

  // Get the raw data
  const data = await response.json();
  console.log('Raw analysis response:', data);
  
  // Make sure we have a requestId - this fixes the "Request ID: null" issue
  if (!data.requestId) {
    console.warn('No requestId in response, generating a fallback ID');
    data.requestId = `fallback-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
  
  // Add the request payload to the result for debugging
  data.requestPayload = requestPayload;
  
  // Normalize the data structure using the same helper function
  return normalizeAnalysisData(data, data.requestId);
}

export async function getLogs(page: number = 1, pageSize: number = 10): Promise<{
  items: LogEntry[];
  total: number;
}> {
  return fetchWithAuth<{ items: LogEntry[]; total: number }>(`/logs?page=${page}&pageSize=${pageSize}`);
}

export async function getAnalysis(requestId: string): Promise<AnalysisResult> {
  try {
    // Add a timestamp to avoid caching
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/analyze/${requestId}?nocache=${timestamp}`, {
      headers: {
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch analysis: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Raw API analysis response for ${requestId}:`, data);
    console.log(`Analysis has salesSnippets: ${!!data.summary?.painPoints}`);
    
    // Normalize the data just like we do with fresh analysis
    return normalizeAnalysisData(data, requestId);
  } catch (error) {
    console.error(`Error in getAnalysis for ${requestId}:`, error);
    throw error;
  }
}

export async function getAnalysisStatus(requestId: string): Promise<{ status: string; progress?: number; urls?: { hasGoodUrls: boolean; goodUrlsCount: number } }> {
  return fetchWithAuth<{ status: string; progress?: number; urls?: { hasGoodUrls: boolean; goodUrlsCount: number } }>(`/status/${requestId}`);
}

/**
 * Get goodUrls separately for a specific analysis job
 * This allows us to get URLs early in the analysis process
 */
export async function getGoodUrls(requestId: string): Promise<{
  goodUrls: GoodUrl[];
  allUrls?: string[];
  status: string;
  completedAt?: string;
}> {
  try {
    console.log(`Fetching goodUrls for ${requestId}...`);
    // Add a timestamp to avoid caching
    const timestamp = new Date().getTime();
    const response = await fetch(`${API_BASE_URL}/analysis/${requestId}/good-urls?nocache=${timestamp}`, {
      headers: {
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      // If not found or pending, return empty array but don't throw
      if (response.status === 404 || response.status === 400) {
        console.log(`No goodUrls found yet for ${requestId} (${response.status})`);
        return {
          goodUrls: [],
          allUrls: [],
          status: 'pending'
        };
      }
      throw new Error(`Failed to fetch goodUrls: ${response.status}`);
    }

    const data = await response.json();
    console.log('GoodURLs response:', data);
    
    // Handle both 'ok' and 'success' status values
    if (data.status === 'ok') {
      data.status = 'success'; // Normalize to 'success' for consistency
    }
    
    return {
      goodUrls: data.goodUrls || [],
      allUrls: data.allUrls || [],
      status: data.status,
      completedAt: data.completedAt
    };
  } catch (error) {
    console.error('Error fetching goodUrls:', error);
    // Return empty array in case of error
    return {
      goodUrls: [],
      allUrls: [],
      status: 'error'
    };
  }
}

export async function getLogEntry(requestId: string): Promise<LogEntry> {
  return fetchWithAuth<LogEntry>(`/logs/${requestId}`);
}

export async function searchLogs(
  query: string,
  page: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponse<LogEntry>> {
  return fetchWithAuth<PaginatedResponse<LogEntry>>(`/logs?query=${query}&page=${page}&pageSize=${pageSize}`);
} 